/**
 * Pointer-driven drag-to-select hook for a day column.
 *
 * Why refs alongside state:
 *   - document-level pointermove/pointerup handlers are attached at drag start
 *   - they capture `preview` via closure → stale value problem
 *   - `previewRef` is the truthful read-side; `preview` (state) drives the React render
 *
 * Why pointer events instead of mouse:
 *   - one API for mouse + touch + pen
 *   - default browser touch behaviors don't compete with our drag
 *
 * Click vs drag is decided at pointerup by comparing start and end minutes:
 * if equal, treat as a click and emit a `DEFAULT_DURATION_MIN` window starting at origin.
 */
import { useCallback, useEffect, useRef, useState, type PointerEvent } from 'react'
import { DEFAULT_DURATION_MIN, MINUTES_PER_DAY, snapMinute, yToMinute } from './timeMath.ts'

interface ActiveDrag {
  date: string
  origin: number
  rect: DOMRect
}

export interface DragPreview {
  date: string
  startMinute: number
  endMinute: number
}

export function useDragSelect(onComplete: (range: DragPreview) => void) {
  const [preview, setPreview] = useState<DragPreview | null>(null)
  const activeRef = useRef<ActiveDrag | null>(null)
  const previewRef = useRef<DragPreview | null>(null)

  useEffect(() => {
    previewRef.current = preview
  }, [preview])

  const startDrag = useCallback(
    (e: PointerEvent<HTMLDivElement>, date: string) => {
      if (e.button !== 0) return
      const column = e.currentTarget
      const rect = column.getBoundingClientRect()
      const origin = snapMinute(yToMinute(e.clientY - rect.top))
      activeRef.current = { date, origin, rect }
      setPreview({ date, startMinute: origin, endMinute: origin })

      function onMove(ev: globalThis.PointerEvent) {
        const a = activeRef.current
        if (!a) return
        const dy = ev.clientY - a.rect.top
        const m = snapMinute(yToMinute(dy))
        const start = Math.min(a.origin, m)
        const end = Math.max(a.origin, m)
        setPreview({ date: a.date, startMinute: start, endMinute: end })
      }

      function onUp() {
        document.removeEventListener('pointermove', onMove)
        document.removeEventListener('pointerup', onUp)
        const cur = previewRef.current
        activeRef.current = null
        setPreview(null)
        if (!cur) return
        const isClick = cur.endMinute === cur.startMinute
        const range: DragPreview = isClick
          ? {
              date: cur.date,
              startMinute: cur.startMinute,
              endMinute: Math.min(MINUTES_PER_DAY, cur.startMinute + DEFAULT_DURATION_MIN),
            }
          : cur
        onComplete(range)
      }

      document.addEventListener('pointermove', onMove)
      document.addEventListener('pointerup', onUp)
      e.preventDefault()
    },
    [onComplete],
  )

  return { preview, startDrag }
}
